import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import Button from "src/components/Button"
import Divider from "src/components/Divider"
import Form from "src/components/Form"
import Heading from "src/components/Heading"

import { createPaymentMethod as createAccountPaymentMethod } from "src/api/Account/PaymentMethods"
import { createPaymentMethod as createGuestPaymentMethod } from "src/api/ContractQuotes/PaymentMethods"
import { validatePaymentMethod } from "src/api/Contracts/SignContract"

import useStripeSetup from "src/hooks/payment_method_hooks/use_stripe_setup"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import ReachOut from "../../ReachOut"
import { SignContractWizardContext } from "../../SignContractWizardContext"
import {
  ELECTRONIC_KINDS,
  PAYMENT_METHOD_TYPE_BY_KIND,
  paymentSelectionComplete,
} from "../../paymentSelection"
import AddMethodForm from "./AddMethodForm"
import CardSurchargeBanner from "./CardSurchargeBanner"
import CashAgreement from "./CashAgreement"
import ComparePanel from "./ComparePanel"
import GuestCapturedPaymentMethod from "./GuestCapturedPaymentMethod"
import ManualBankWarning from "./ManualBankWarning"
import MethodTiles from "./MethodTiles"
import SavedMethodSelect from "./SavedMethodSelect"
import WhatHappensNextPanel from "./WhatHappensNextPanel"

const NOT_FINALIZED = "payment_method_not_finalized"

// Stripe's own errors carry a type, and our finalise endpoints answer a 4xx
// with copy meant for the boater; anything else is not theirs to read. Once
// Stripe has confirmed the intent the card cannot be edited, only retried or
// replaced, so that failure gets its own code.
const saveFailure = (failure, intentConfirmed, wizardCopy) => {
  const stripeError = typeof failure?.type === "string"
  const refusedByServer =
    Number.isInteger(failure?.status) &&
    failure.status >= 400 &&
    failure.status < 500
  const message = failure?.message
  const ownMessage =
    (stripeError || refusedByServer) && typeof message === "string"
      ? message
      : null

  return intentConfirmed
    ? {
        code: NOT_FINALIZED,
        message: ownMessage ?? wizardCopy.paymentSaveRetry,
      }
    : {
        code: "payment_method_not_saved",
        message: ownMessage ?? wizardCopy.paymentSaveFailed,
      }
}

const PaymentMethodStep = () => {
  const {
    dispatch,
    state,
    quote,
    marina,
    isGuest,
    surcharge,
    reachOut,
    contractChatUrl,
    wizardCopy,
    steps,
  } = useContext(SignContractWizardContext)
  const tracker = useTracker()
  const showToast = useToast()
  const { acceptedPaymentMethods, allowDirectPayment } = quote
  const {
    paymentMethodKind: kind,
    paymentMethod,
    paymentMethods,
    paymentCheck,
    paymentMethodError,
  } = state

  const nextStep = steps[state.currentStep]
  const stepValid = paymentSelectionComplete(state, quote)

  const [checking, setChecking] = useState(false)
  const [formComplete, setFormComplete] = useState(false)
  const [formGeneration, setFormGeneration] = useState(0)
  const addFormRef = useRef(null)
  const handleFormChange = useCallback(
    () => dispatch({ type: "PAYMENT_METHOD_EDITED" }),
    [dispatch]
  )
  // A succeeded SetupIntent cannot be confirmed again, so a different payment
  // method means a fresh form and a fresh intent.
  const startOver = useCallback(() => {
    setFormGeneration((generation) => generation + 1)
    handleFormChange()
  }, [handleFormChange])
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const electronic = ELECTRONIC_KINDS.includes(kind)
  const capturedPaymentMethod = isGuest
    ? paymentMethods.find((pm) => pm.stripePaymentMethodId === paymentMethod)
    : undefined
  const showAddForm =
    electronic &&
    (paymentMethod === "add" || (isGuest && !capturedPaymentMethod))
  const canContinue = stepValid || (showAddForm && formComplete)
  const saveNotFinalized = paymentMethodError?.code === NOT_FINALIZED

  const createFn = useMemo(
    () =>
      isGuest
        ? createGuestPaymentMethod(quote.encodedId)
        : createAccountPaymentMethod,
    [isGuest, quote.encodedId]
  )
  const { paymentMethod: newPaymentMethod, error: setupError } =
    useStripeSetup(createFn)
  const methodAddedRef = useRef(false)

  const recordAddedMethod = useCallback(
    (added) => {
      tracker.trackEvent("contracts_v2:payment_method_added", {
        contract_quote_id: quote.id,
        marina_id: marina.id,
        marina_name: marina.name,
        payment_method_type: added.type,
      })

      methodAddedRef.current = true
      dispatch({ type: "PAYMENT_METHOD_ADDED", payload: added })
    },
    [dispatch, quote, marina, tracker]
  )

  useEffect(() => {
    if (newPaymentMethod) recordAddedMethod(newPaymentMethod)
  }, [newPaymentMethod, recordAddedMethod])

  useEffect(() => {
    if (setupError) {
      showToast(setupError.message, { type: "error" })
    }
  }, [setupError, showToast])

  const handleContinue = async () => {
    if (checking) return

    setChecking(true)
    try {
      let { paymentType, paymentMethod: paymentMethodId } = state

      if (showAddForm) {
        let saved
        try {
          saved = await addFormRef.current?.submit()
          if (!saved) throw new Error("payment form not ready")
        } catch (failure) {
          if (!mountedRef.current) return
          dispatch({
            type: "PAYMENT_METHOD_REFUSED",
            payload: saveFailure(
              failure,
              Boolean(addFormRef.current?.intentConfirmed()),
              wizardCopy
            ),
          })
          return
        }
        recordAddedMethod(saved)
        if (!mountedRef.current) return
        paymentType = "credit"
        paymentMethodId = saved.stripePaymentMethodId
      }

      const result = await validatePaymentMethod({
        quoteId: quote.encodedId,
        paymentType,
        paymentMethodId,
      })
      if (!mountedRef.current) return
      if (result.usable) {
        const { kind: resultKind, funding, surchargeApplies } = result
        dispatch({
          type: "PAYMENT_METHOD_CHECKED",
          payload: {
            usable: true,
            kind: resultKind,
            funding,
            surchargeApplies,
          },
        })
        dispatch({ type: "GO_TO_NEXT_STEP" })
      } else {
        dispatch({
          type: "PAYMENT_METHOD_REFUSED",
          payload: {
            code: result.code,
            message: result.error ?? wizardCopy.paymentCheckFailed,
          },
        })
      }
    } catch {
      if (!mountedRef.current) return
      dispatch({
        type: "PAYMENT_METHOD_REFUSED",
        payload: {
          code: "payment_check_unavailable",
          message: wizardCopy.paymentCheckFailed,
        },
      })
    } finally {
      if (mountedRef.current) setChecking(false)
    }
  }

  const kinds = useMemo(
    () => [
      ...ELECTRONIC_KINDS.filter((electronicKind) =>
        acceptedPaymentMethods.includes(electronicKind)
      ),
      ...(allowDirectPayment ? ["manual"] : []),
    ],
    [acceptedPaymentMethods, allowDirectPayment]
  )

  useEffect(() => {
    if (kinds.length === 1 && kind !== kinds[0]) {
      dispatch({ type: "PAYMENT_METHOD_KIND_SELECTED", payload: kinds[0] })
    }
  }, [kinds, kind, dispatch])

  const trackSelected = (paymentMethodType) =>
    tracker.trackEvent("contracts_v2:payment_method_selected", {
      contract_quote_id: quote.id,
      marina_id: marina.id,
      marina_name: marina.name,
      payment_method_type: paymentMethodType,
    })

  const handleKindSelect = (selectedKind) => {
    if (selectedKind === "manual") trackSelected("manual")

    dispatch({ type: "PAYMENT_METHOD_KIND_SELECTED", payload: selectedKind })

    const capturedOfKind = isGuest
      ? paymentMethods
          .filter((pm) => pm.type === PAYMENT_METHOD_TYPE_BY_KIND[selectedKind])
          .pop()
      : undefined
    if (capturedOfKind) {
      dispatch({
        type: "PAYMENT_METHOD_SELECTED",
        payload: capturedOfKind.stripePaymentMethodId,
      })
    }
  }

  const handleSavedMethodChange = (stripePaymentMethodId) => {
    const selected = paymentMethods.find(
      (pm) => pm.stripePaymentMethodId === stripePaymentMethodId
    )

    if (selected) trackSelected(selected.type)

    dispatch({
      type: "PAYMENT_METHOD_SELECTED",
      payload: stripePaymentMethodId,
    })
  }

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <Heading.HeroTitle>
          {wizardCopy.addPaymentMethodTitle}
        </Heading.HeroTitle>
        <p className="text-base font-semibold text-gray-900">
          {wizardCopy.addPaymentMethodSubtitle}
        </p>
      </div>

      <div className="hidden lg:block [&>hr]:my-0">
        <Divider />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="flex flex-col space-y-6 lg:col-span-2">
          <MethodTiles
            kinds={kinds}
            selected={kind}
            onSelect={handleKindSelect}
            copy={wizardCopy}
          />

          {electronic && !isGuest && (
            <SavedMethodSelect
              kind={kind}
              paymentMethods={paymentMethods}
              value={paymentMethod}
              onChange={handleSavedMethodChange}
              copy={wizardCopy}
            />
          )}

          {showAddForm && (
            <AddMethodForm
              key={`${kind}:${formGeneration}`}
              ref={addFormRef}
              kind={kind}
              methodAddedRef={methodAddedRef}
              onChange={handleFormChange}
              onCompleteChange={setFormComplete}
              readOnly={saveNotFinalized}
            />
          )}

          {electronic && capturedPaymentMethod && (
            <GuestCapturedPaymentMethod
              paymentMethod={capturedPaymentMethod}
              dispatch={dispatch}
              copy={wizardCopy}
            />
          )}

          {showAddForm && !isGuest && kind === "us_bank_account" && (
            <ManualBankWarning copy={wizardCopy} />
          )}

          {kind === "card" && (
            <CardSurchargeBanner
              surcharge={surcharge}
              paymentCheck={paymentCheck}
            />
          )}

          {kind === "manual" && <CashAgreement copy={wizardCopy} />}

          {paymentMethodError && (
            <Form.Error>{paymentMethodError.message}</Form.Error>
          )}

          {showAddForm && saveNotFinalized && (
            <div>
              <Button variant="tertiary" onClick={startOver}>
                {wizardCopy.useDifferentPaymentMethod}
              </Button>
            </div>
          )}

          <Button
            variant="primary"
            fullWidth
            disabled={!canContinue}
            isLoading={checking}
            onClick={handleContinue}
          >
            {nextStep
              ? `${wizardCopy.continueTo} ${nextStep.title}`
              : wizardCopy.continue}
          </Button>

          <p className="flex items-center justify-center gap-2 text-gray-700">
            <i className="fa-solid fa-shield-check text-teal-600" />
            <span>{wizardCopy.securePayment}</span>
            <i className="fa-solid fa-lock" />
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          {kind === "card" && (
            <ComparePanel
              acceptedPaymentMethods={acceptedPaymentMethods}
              allowDirectPayment={allowDirectPayment}
              copy={wizardCopy}
            />
          )}
          {kind === "manual" && <WhatHappensNextPanel copy={wizardCopy} />}
          <ReachOut
            reachOut={reachOut}
            contractChatUrl={contractChatUrl}
            marinaId={marina.id}
            marinaName={marina.name}
            quoteId={quote.id}
          />
        </div>
      </div>
    </div>
  )
}

export default PaymentMethodStep
